import { format } from 'date-fns'
import * as XLSX from 'xlsx'

export interface DailyWorkRecord {
  id: string
  date: string
  title: string
  description: string
  hoursWorked: number
  projectId: string | null
  user: {
    id: string
    name: string
    email: string
    employeeId: string | null
    department: string | null
  }
  createdAt?: string
  updatedAt?: string
}

export interface ExportDailyWorkOptions {
  records: DailyWorkRecord[]
  filterType: 'employee' | 'date' | 'all'
  employeeName?: string
  dateRange?: {
    start: string
    end: string
  }
}

export function exportDailyWorkToExcel(options: ExportDailyWorkOptions): void {
  const { records, filterType, employeeName, dateRange } = options

  // Prepare data for Excel
  const data = records.map(record => ({
    'Date': format(new Date(record.date + 'T00:00:00'), 'dd/MM/yyyy'),
    'Employee Name': record.user?.name || 'Unknown',
    'Employee ID': record.user?.employeeId || '',
    'Department': record.user?.department || '',
    'Work Title': record.title,
    'Description': record.description,
    'Hours Worked': record.hoursWorked,
    'Project ID': record.projectId || '',
    'Created At': record.createdAt ? format(new Date(record.createdAt), 'dd/MM/yyyy HH:mm') : '',
    'Updated At': record.updatedAt ? format(new Date(record.updatedAt), 'dd/MM/yyyy HH:mm') : '',
  }))

  // Filter data based on filter type
  let filteredData = data
  if (filterType === 'employee' && employeeName) {
    filteredData = data.filter(row => row['Employee Name'] === employeeName)
  } else if (filterType === 'date' && dateRange) {
    const startDate = new Date(dateRange.start + 'T00:00:00')
    const endDate = new Date(dateRange.end + 'T00:00:00')
    filteredData = data.filter(row => {
      const rowDate = new Date(row['Date'] + 'T00:00:00')
      return rowDate >= startDate && rowDate <= endDate
    })
  }

  // Generate filename
  const timestamp = new Date().toISOString().split('T')[0]
  const employeePart = filterType === 'employee' && employeeName ? `_${employeeName.replace(/\s+/g, '_')}` : ''
  const datePart = filterType === 'date' ? `_${dateRange?.start}_to_${dateRange?.end}` : ''
  const filename = `Daily_Work_Records${employeePart}${datePart}_${timestamp}.xlsx`

  // Create workbook
  const ws = XLSX.utils.json_to_sheet(filteredData)
  
  // Auto-size columns
  const colWidths = Object.keys(filteredData[0] || {}).map(key => ({
    wch: Math.max(key.length, 15) // Min width of 15 characters
  }))
  ws['!cols'] = colWidths

  // Add headers
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Daily Work Records')

  // Add summary sheet
  if (filteredData.length > 0) {
    const summary = generateSummary(filteredData)
    const summaryWs = XLSX.utils.json_to_sheet([summary])
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary')
  }

  // Generate and download file
  XLSX.writeFile(wb, filename)

  // Show success notification
  console.log(`Successfully exported ${filteredData.length} daily work records to Excel`)
}

function generateSummary(data: any[]) {
  const totalHours = data.reduce((sum, row) => sum + (row['Hours Worked'] || 0), 0)
  const uniqueEmployees = new Set(data.map(row => row['Employee Name'])).size
  const uniqueDepartments = new Set(data.map(row => row['Department'])).size
  const dateRange = getDateRange(data)

  return {
    'Total Records': data.length,
    'Total Hours Worked': totalHours.toFixed(2),
    'Unique Employees': uniqueEmployees,
    'Unique Departments': uniqueDepartments,
    'Date Range': dateRange,
    'Average Hours per Record': (totalHours / data.length).toFixed(2),
    'Export Date': format(new Date(), 'dd/MM/yyyy HH:mm')
  }
}

function getDateRange(data: any[]): string {
  if (data.length === 0) return ''
  
  const dates = data.map(row => new Date(row['Date'] + 'T00:00:00'))
  const minDate = new Date(Math.min(...dates.map(d => d.getTime())))
  const maxDate = new Date(Math.max(...dates.map(d => d.getTime())))
  
  return `${format(minDate, 'dd/MM/yyyy')} - ${format(maxDate, 'dd/MM/yyyy')}`
}

export function exportDailyWorkByEmployeeToExcel(records: DailyWorkRecord[]): void {
  // Group records by employee
  const employeeGroups = records.reduce((groups, record) => {
    const employeeName = record.user?.name || 'Unknown'
    if (!groups[employeeName]) {
      groups[employeeName] = []
    }
    groups[employeeName].push(record)
    return groups
  }, {} as Record<string, DailyWorkRecord[]>)

  // Create separate worksheet for each employee
  const data = Object.entries(employeeGroups).map(([employeeName, employeeRecords]) => {
    const employeeData = employeeRecords.map(record => ({
      'Date': format(new Date(record.date + 'T00:00:00'), 'dd/MM/yyyy'),
      'Work Title': record.title,
      'Description': record.description,
      'Hours Worked': record.hoursWorked,
      'Project ID': record.projectId || '',
      'Department': record.user.department || '',
    }))

    return {
      sheetName: employeeName,
      data: employeeData
    }
  })

  // Generate filename
  const timestamp = new Date().toISOString().split('T')[0]
  const filename = `Daily_Work_By_Employee_${timestamp}.xlsx`

  // Create workbook
  const wb = XLSX.utils.book_new()

  // Add sheets for each employee
  data.forEach(({ sheetName, data: employeeData }) => {
    if (employeeData.length > 0) {
      const ws = XLSX.utils.json_to_sheet(employeeData)
      
      // Auto-size columns
      const colWidths = Object.keys(employeeData[0]).map(key => ({
        wch: Math.max(key.length, 15)
      }))
      ws['!cols'] = colWidths

      XLSX.utils.book_append_sheet(wb, ws, sheetName)
    }
  })

  // Add summary sheet
  const summary = generateEmployeeSummary(employeeGroups)
  const summaryWs = XLSX.utils.json_to_sheet([summary])
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary')

  // Generate and download file
  XLSX.writeFile(wb, filename)

  // Show success notification
  const totalEmployees = Object.keys(employeeGroups).length
  const totalRecords = records.length
  console.log(`Successfully exported ${totalEmployees} employees with ${totalRecords} daily work records to Excel`)
}

function generateEmployeeSummary(employeeGroups: Record<string, DailyWorkRecord[]>) {
  const summaryData = Object.entries(employeeGroups).map(([name, records]) => {
    const totalHours = records.reduce((sum, record) => sum + record.hoursWorked, 0)
    const totalDays = new Set(records.map(record => record.date)).size
    const avgHoursPerDay = totalDays > 0 ? totalHours / totalDays : 0
    
    return {
      'Employee Name': name,
      'Total Records': records.length,
      'Total Hours Worked': totalHours.toFixed(2),
      'Total Days Worked': totalDays,
      'Average Hours per Day': avgHoursPerDay.toFixed(2),
      'Department': records[0]?.user?.department || '',
    }
  })

  return summaryData
}